import 'react-datepicker/dist/react-datepicker.css';

import { Card, Col, Dropdown, Input, Row, Statistic, Table, Typography } from 'antd';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  FaAngleDown,
  FaCalendarAlt,
  FaChartLine,
  FaMoneyBillWave,
  FaSearch,
  FaUsers,
} from 'react-icons/fa';
import { ListPlayerGrid, ListPlayerGridMobile } from '~/data/dummy';
import { baseUrl, formatNumber, getDevice } from '~/utils/helper';
import { useCallback, useEffect, useState } from 'react';

import AddUserForm from './AddUserForm';
import { BetHistory } from '~/components/Action';
import DetailsReportMobile from './DetailsReportMobile';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ModalUpdateWallet from './ModalUpdateWallet';
import { RangeDatePicker } from '~/components/common';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTableSearch } from '~/Hooks';
import { useTranslation } from 'react-i18next';
import vi from 'date-fns/locale/vi';
import { useStateContext } from '~/contexts/ContextProvider';

const { Search } = Input;
const { Text } = Typography;
registerLocale('vi', vi);

const Report = ({ title }) => {
  const device = getDevice();
  const { formatMoney, ref } = useStateContext();
  const { t } = useTranslation('report');
  const [startDate, setStartDate] = useState(
    dayjs().add(-7, 'd').format('YYYY-MM-DD') + ' 00:00:00',
  );
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD') + ' 23:59:59');
  const [dataUser, setDataUser] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const [isLoad, setIsLoad] = useState(true);

  document.title = title;

  useEffect(() => {
    axios
      .get(`${baseUrl}/api/daily/report`, {
        params: {
          start: startDate,
          end: endDate,
        },
        headers: {
          'x-access-token': sessionStorage.getItem('auth'),
          'Access-Control-Allow-Origin': '*',
        },
      })
      .then((res) => {
        setDataUser(res.data);
        setIsLoad(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error(t('common:error'), { them: 'light' });
        setIsLoad(false);
      });
  }, []);

  const fetchUsers = useCallback(async () => {
    const data = dataUser;
    return { data };
  }, [dataUser]);

  const { filteredData, loading } = useTableSearch({
    searchVal,
    retrieve: fetchUsers,
  });

  const handleSearch = () => {
    setIsLoad(true);
    axios
      .get(`${baseUrl}/api/daily/report`, {
        params: {
          start: startDate,
          end: endDate,
        },
        headers: {
          'x-access-token': sessionStorage.getItem('auth'),
          'Access-Control-Allow-Origin': '*',
        },
      })
      .then((res) => {
        setDataUser(res.data);
        setIsLoad(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error(t('common:error'), { them: 'light' });
        setIsLoad(false);
      });
  };

  const handleChangeDate = (v) => {
    setStartDate(v[0]);
    setEndDate(v[1]);
  };

  const createMenuItems = (data) => {
    return [
      {
        key: '1',
        label: (
          <div className="w-fit">
            <BetHistory data={data} type="label" />
          </div>
        ),
      },
      {
        key: '2',
        label: <ModalUpdateWallet record={data} typeButton="label" reload={handleSearch} />,
      },
    ];
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    const totalUsers = filteredData?.length || 0;
    const totalRecharge =
      filteredData?.reduce((sum, item) => sum + (Number(item.totalRecharge) || 0), 0) || 0;
    const totalWithdraw =
      filteredData?.reduce((sum, item) => sum + (Number(item.totalWithdraw) || 0), 0) || 0;
    const totalBetHistory =
      filteredData?.reduce((sum, item) => sum + (Number(item.totalBetHistory) || 0), 0) || 0;

    return { totalUsers, totalRecharge, totalWithdraw, totalBetHistory };
  };

  const { totalUsers, totalRecharge, totalWithdraw, totalBetHistory } = calculateSummary();

  const ListPlayerGrid = () => {
    const { t } = useTranslation('report');
    return [
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <FaUsers className="text-blue-500" />
            {t('username')}
          </div>
        ),
        dataIndex: 'username',
        render: (_, data) => (
          <span className={`font-medium ${data?.createdBy ? 'text-green-600' : 'text-gray-800'}`}>
            {_}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <FaChartLine className="text-purple-500" />
            {t('level')}
          </div>
        ),
        dataIndex: 'level',
        render: (_, data) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              data?.createdBy ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
            {_}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <FaMoneyBillWave className="text-green-500" />
            {t('sotiennap')}
          </div>
        ),
        dataIndex: 'totalRecharge',
        render: (data, record) => (
          <span
            className={`font-semibold ${record?.createdBy ? 'text-green-600' : 'text-blue-600'}`}>
            {formatMoney(data)}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <FaMoneyBillWave className="text-red-500" />
            {t('sotienrut')}
          </div>
        ),
        dataIndex: 'totalWithdraw',
        render: (data, record) => (
          <span
            className={`font-semibold ${record?.createdBy ? 'text-green-600' : 'text-red-600'}`}>
            {formatMoney(data)}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            ⚙️ {t('dieuchinhbangtay')}
          </div>
        ),
        dataIndex: 'handEdit',
        render: (data, record) => (
          <span className={`font-medium ${data >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatMoney(data)}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            🎯 {t('sotiendatcuoc')}
          </div>
        ),
        dataIndex: 'totalBetHistory',
        render: (data, record) => (
          <span
            className={`font-semibold ${record?.createdBy ? 'text-green-600' : 'text-purple-600'}`}>
            {formatMoney(data)}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            📊 {t('thangthua')}
          </div>
        ),
        dataIndex: ['', 'id'],
        render: (_, data) => {
          const result = data.totalBetWin - data.totalBetLose;
          return (
            <div className="flex flex-col gap-1">
              <span className={` ${result > 0 ? 'text-green-600' : 'text-red-500'} font-medium`}>
                {formatMoney(result)}
              </span>
              {/* <span className="text-xs font-medium text-red-600">
              Thua: {formatMoney(data.totalBetLose)}
            </span> */}
            </div>
          );
        },
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            🔄 {t('hoantra')}
          </div>
        ),
        dataIndex: 'totalRefund',
        render: (_, data) => <span className="font-medium text-orange-600">{formatMoney(_)}</span>,
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            🎁 {t('khuyenmai')}
          </div>
        ),
        dataIndex: 'totalGiftCode',
        render: (_, data) => <span className="font-medium text-pink-600">{formatMoney(_)}</span>,
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">💰 {t('sodu')}</div>
        ),
        dataIndex: 'money',
        render: (_, data) => (
          <span className={`font-bold ${_ >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatMoney(_)}
          </span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            📅 {t('ngaydangky')}
          </div>
        ),
        dataIndex: 'created_at',
        render: (data, record) => (
          <span className="text-sm text-gray-600">{dayjs(data).format('DD/MM/YYYY')}</span>
        ),
      },
      {
        title: (
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            ⚡ {t('hanhdong')}
          </div>
        ),
        width: '130px',
        dataIndex: ['', 'id'],
        render: (_, data) => <BetHistory data={data} />,
      },
    ];
  };

  const ListPlayerGridMobile = () => {
    const { t } = useTranslation('report');
    return [
      {
        title: t('username'),
        dataIndex: 'username',
      },
      {
        title: t('sodu'),
        dataIndex: 'money',
        render: (data) => <span className="font-semibold text-green-600">{formatMoney(data)}</span>,
      },
      {
        title: t('hanhdong'),
        dataIndex: ['', 'id'],
        render: (_, data) => <DetailsReportMobile data={data} getData={handleSearch} />,
      },
    ];
  };

  const listPlayerGrid = device === 'mobile' ? ListPlayerGridMobile() : ListPlayerGrid();

  return (
    <div
      className="min-h-screen p-2 md:p-6"
      style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}>
      {/* Header Section */}
      <div
        className="p-2 mb-4 md:p-6 rounded-2xl"
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="hidden md:block">
            <h1 className="mb-2 text-3xl font-bold text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text">
              {t('bao_cao_dai_ly')}
            </h1>
            <p className="text-gray-600">{t('ql_va_td_dhcctv')}</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex items-center gap-2">
              {/* <FaCalendarAlt className="text-blue-500" /> */}
              <RangeDatePicker
                onChange={handleChangeDate}
                className="w-full h-12 border-2 rounded-xl border-blue-500/20 bg-white/90"
              />
            </div>

            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-6 py-2 font-semibold text-white transition-all duration-300 md:py-3 rounded-xl hover:scale-105 hover:shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              }}>
              <FaSearch size={16} />
              {t('common:search')}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={12} sm={12} lg={6}>
          <Card
            className="text-center transition-all duration-300 hover:scale-105 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
            }}>
            <Statistic
              title={<span className="text-white opacity-90">👥 {t('tong_thanh_vien')}</span>}
              value={totalUsers}
              formatter={(value) => formatNumber(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card
            className="text-center transition-all duration-300 hover:scale-105 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]"
            style={{
              background: 'linear-gradient(135deg, #43e97b 0%, #14bd80 100%)',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
            }}>
            <Statistic
              title={<span className="text-white opacity-90">💰 {t('tong_nap')}</span>}
              value={totalRecharge}
              formatter={(value) => formatMoney(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card
            className="text-center transition-all duration-300 hover:scale-105 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]"
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
            }}>
            <Statistic
              title={<span className="text-white opacity-90">💸 {t('tong_rut')}</span>}
              value={totalWithdraw}
              formatter={(value) => formatMoney(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card
            className="text-center transition-all duration-300 hover:scale-105 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]"
            style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              border: 'none',
              borderRadius: '16px',
              color: 'white',
            }}>
            <Statistic
              title={<span className="text-white opacity-90">🎯 {t('tong_cuoc')}</span>}
              value={totalBetHistory}
              formatter={(value) => formatMoney(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Data Table Section */}
      <div
        className="p-2 md:p-6 rounded-2xl"
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
        <div className="mb-6">
          <Search
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder={`🔍 ${t('timkiemnguoichoi')}`}
            // enterButton={
            //   <div
            //     className="flex items-center gap-2 px-4 py-2 font-medium text-white rounded-lg"
            //     style={{
            //       background:
            //         "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            //     }}
            //   >
            //     <FaSearch size={14} />
            //     Tìm kiếm
            //   </div>
            // }
            size="large"
            className="shadow-sm"
            style={{
              borderRadius: '12px',
            }}
          />
        </div>

        <Table
          rowKey="id"
          dataSource={filteredData}
          columns={listPlayerGrid}
          loading={isLoad}
          className="custom-table"
          bordered={false}
          size="middle"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t('cuar')} ${total} ${t('muc')}`,
            style: {
              marginTop: '24px',
            },
          }}
          scroll={{ x: 'max-content' }}
          rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        />
      </div>

      <style jsx>{`
        .custom-table .ant-table-thead > tr > th {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          color: #374151 !important;
          padding: 16px !important;
        }

        .custom-table .ant-table-tbody > tr > td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f1f5f9 !important;
        }

        .table-row-light {
          background-color: #ffffff !important;
        }

        .table-row-dark {
          background-color: #f8fafc !important;
        }

        .custom-table .ant-table-tbody > tr:hover > td {
          background-color: #f0f9ff !important;
        }

        .ant-input-search .ant-btn {
          border-radius: 0 12px 12px 0 !important;
          height: 48px !important;
          border: none !important;
        }

        .ant-input-search .ant-input {
          border-radius: 12px 0 0 12px !important;
          height: 48px !important;
          border-right: none !important;
        }

        .ant-pagination .ant-pagination-item {
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
        }

        .ant-pagination .ant-pagination-item-active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border-color: transparent !important;
        }

        .ant-pagination .ant-pagination-item-active a {
          color: white !important;
        }
      `}</style>
    </div>
  );
};

export default Report;
